import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Stack, Card, Group, Text, Badge, Button, Title } from '@repletoryreach/sdk-ui';
import {
  Alert,
  DataTable,
  EmptyState,
  EntityCardGrid,
  KpiFilterRow,
  PageHeader,
  TableShell,
  type KpiFilterItem,
  type ViewMode
} from '@repletoryreach/sdk-ui/data-display';
import { DonutChart } from '@repletoryreach/sdk-ui/charts';
import { media } from '@repletoryreach/sdk-api';
import type { admin } from '@repletoryreach/sdk-api';
import { FilterDrawer, type FilterField, type FilterValues } from '@repletoryreach/sdk-ui/data-display';
import { usePersistedState } from '../../lib/usePersistedState';
import { formatInteger, humanBytes } from '../../lib/format';
import type { Indexed } from '../../lib/types';
import { TenantName } from '../../components/TenantName';
import { StatCard } from '../../components/StatCard';
import { ActiveFilterPills, type ActiveFilterPill } from '../../components/ActiveFilterPills';
import { TableFooter } from '../../components/TableFooter';
import { RowMenu } from '../../components/RowMenu';
import { useTenantMap } from '../../data/useTenants';
import { formatDateTime } from '../../lib/format';
import { useT } from '../../i18n/I18nProvider';
import { ScanPanel } from './components/ScanPanel';

const MUTED = 'var(--rr-color-text-muted)';
type Row = admin.MediaStatsRow;
type MimeRow = admin.MediaStatsByMime;
type KpiKey = 'all' | 'large' | 'extracted' | 'no_text';

const MB = 1024 * 1024;
const LARGE_THRESHOLD = 100 * MB;
const MEDIUM_THRESHOLD = 10 * MB;

/** The service returns the 200 heaviest tenants; see `capHint`. */
const TENANT_LIMIT = 200;

/** 18px line icons for the stat tiles. The tint comes from the card's tone. */
const icon = (path: React.ReactNode) => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    {path}
  </svg>
);
const FileIcon = icon(<><path d="M11.5 2.5H6a1.5 1.5 0 0 0-1.5 1.5v12A1.5 1.5 0 0 0 6 17.5h8a1.5 1.5 0 0 0 1.5-1.5V6.5z" /><path d="M11.5 2.5v4h4" /></>);
const StackIcon = icon(<><ellipse cx="10" cy="5" rx="6" ry="2.4" /><path d="M4 5v5c0 1.3 2.7 2.4 6 2.4s6-1.1 6-2.4V5" /><path d="M4 10v5c0 1.3 2.7 2.4 6 2.4s6-1.1 6-2.4v-5" /></>);
const TextIcon = icon(<><rect x="3.5" y="3.5" width="13" height="13" rx="2" /><path d="M6.5 8h7M6.5 11h7M6.5 14h4" /></>);
const UploadIcon = icon(<><path d="M10 14V5" /><path d="M6.5 8.5 10 5l3.5 3.5" /><path d="M4 15.5h12" /></>);
const BytesIcon = icon(<><circle cx="10" cy="10" r="6.5" /><path d="M10 6v4l2.5 2" /></>);

export default function MediaPage() {
  const t = useT();
  const nav = useNavigate();
  // The canonical path, rather than the `/v1/admin/media/stats` alias the page
  // used to call. Both are mapped to the same handler and return the same body.
  const { data, isLoading, error, refetch, isFetching, dataUpdatedAt } = useQuery({
    queryKey: ['admin', 'media', 'stats'],
    queryFn: () => media.stats(),
    staleTime: 60_000
  });

  const stats = data?.data;
  const all = useMemo(() => (stats?.by_tenant ?? []) as Row[], [stats]);
  const byMime = useMemo(() => (stats?.by_mime ?? []) as MimeRow[], [stats]);
  const { map: tenants } = useTenantMap();

  const [view, setView] = usePersistedState<ViewMode>('admin.media.view', 'table');
  const [pageSize, setPageSize] = usePersistedState<number>('admin.media.pageSize', 25);

  const [query, setQuery] = useState('');
  const [kpi, setKpi] = useState<KpiKey | null>('all');
  const [filters, setFilters] = useState<FilterValues>({});
  const [page, setPage] = useState(1);

  const counts = useMemo(() => ({
    all: all.length,
    large: all.filter((r) => r.total_bytes >= LARGE_THRESHOLD).length,
    extracted: all.filter((r) => r.with_text > 0).length,
    // The old "Empty" chip counted `file_count === 0`, which the handler's
    // GROUP BY over stored objects can never produce: it was a chip that could
    // not light up. Tenants holding files with no extracted text is the real
    // and actionable version of the same question.
    no_text: all.filter((r) => r.with_text === 0).length
  }), [all]);

  const kpiItems: KpiFilterItem[] = [
    { id: 'all', label: t('common.all'), value: counts.all, tone: 'primary' },
    { id: 'large', label: t('media.kpi.large'), value: counts.large, tone: 'warning' },
    { id: 'extracted', label: t('media.withExtractedText'), value: counts.extracted, tone: 'healthy' },
    { id: 'no_text', label: t('media.kpi.noText'), value: counts.no_text, tone: 'inactive' }
  ];

  const filterFields: FilterField[] = useMemo(() => [
    {
      key: 'size',
      label: t('media.filters.size'),
      placeholder: t('media.filters.anySize'),
      options: [
        { value: 'small', label: t('media.filters.sizeSmall') },
        { value: 'medium', label: t('media.filters.sizeMedium') },
        { value: 'large', label: t('media.filters.sizeLarge') }
      ]
    }
  ], [t]);

  const drawerActive = Object.values(filters).filter((v) => v && v !== 'all').length;
  const hasActiveFilters = Boolean(query.trim()) || (kpi !== null && kpi !== 'all') || drawerActive > 0;
  function clearAllFilters() { setQuery(''); setKpi('all'); setFilters({}); setPage(1); }

  // Each narrowing named on the page rather than counted on a button.
  const pills: ActiveFilterPill[] = [];
  if (query.trim()) pills.push({ id: 'query', field: t('common.search'), value: query.trim() });
  if (kpi && kpi !== 'all') {
    pills.push({ id: 'kpi', field: t('common.view'), value: kpiItems.find((i) => i.id === kpi)?.label ?? kpi });
  }
  if (filters.size) {
    const labels: Record<string, string> = {
      small: t('media.filters.sizeSmall'),
      medium: t('media.filters.sizeMedium'),
      large: t('media.filters.sizeLarge')
    };
    pills.push({ id: 'size', field: t('media.filters.size'), value: labels[filters.size] ?? filters.size });
  }

  function removePill(id: string) {
    setPage(1);
    if (id === 'query') return setQuery('');
    if (id === 'kpi') return setKpi('all');
    setFilters((current) => {
      const next = { ...current };
      delete next[id];
      return next;
    });
  }

  const filtered = useMemo(() => {
    let rows = all;
    if (kpi === 'large') rows = rows.filter((r) => r.total_bytes >= LARGE_THRESHOLD);
    if (kpi === 'extracted') rows = rows.filter((r) => r.with_text > 0);
    if (kpi === 'no_text') rows = rows.filter((r) => r.with_text === 0);
    if (filters.size === 'small') rows = rows.filter((r) => r.total_bytes < MEDIUM_THRESHOLD);
    if (filters.size === 'medium') rows = rows.filter((r) => r.total_bytes >= MEDIUM_THRESHOLD && r.total_bytes < LARGE_THRESHOLD);
    if (filters.size === 'large') rows = rows.filter((r) => r.total_bytes >= LARGE_THRESHOLD);
    const q = query.trim().toLowerCase();
    if (q) rows = rows.filter((r) => (tenants.get(r.organisation_id)?.name ?? '').toLowerCase().includes(q));
    return rows;
  }, [all, kpi, filters, query, tenants]);

  useEffect(() => { if (page > 1 && (page - 1) * pageSize >= filtered.length) setPage(1); }, [filtered.length, page, pageSize]);
  const paged = useMemo(() => filtered.slice((page - 1) * pageSize, page * pageSize), [filtered, page, pageSize]);

  const capped = all.length >= TENANT_LIMIT;
  // A metric card must not assert a confident zero while the request is in
  // flight or after it failed: those are different states from "none stored".
  const unknown = isLoading || Boolean(error);
  const metric = (value: string) => (unknown ? t('common.notAvailable') : value);

  const mimeSlices = useMemo(
    () => byMime.slice(0, 8).map((m) => ({ name: m.mime_type, value: m.total_bytes })),
    [byMime]
  );

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.section.messaging')}
        title={t('media.title')}
        description={t('media.description')}
        actions={
          <Stack gap="xs" align="flex-end">
            <Button size="xs" variant="outline" loading={isFetching} onClick={() => void refetch()}>
              {t('action.refresh')}
            </Button>
            <Text role="caption" c={MUTED}>{t('common.lastUpdated', { time: formatDateTime(new Date(dataUpdatedAt).toISOString()) })}</Text>
          </Stack>
        }
      />

      {/* Five figures with a tinted icon each: the row that answers "what is
          the shape of this data" before the table says whose it is. The hint
          under each keeps a number from standing on its own. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
        <StatCard
          loading={isLoading}
          tone="primary"
          icon={FileIcon}
          label={t('media.metrics.totalFiles')}
          value={metric(formatInteger(stats?.total_files ?? 0))}
          hint={unknown ? undefined : t('media.metrics.acrossTenants', { count: formatInteger(all.length) })}
        />
        <StatCard
          loading={isLoading}
          tone="info"
          icon={StackIcon}
          label={t('media.metrics.totalBytes')}
          value={metric(humanBytes(stats?.total_bytes ?? 0))}
          hint={unknown ? undefined : t('media.metrics.acrossTenants', { count: formatInteger(all.length) })}
        />
        <StatCard
          loading={isLoading}
          tone="success"
          icon={TextIcon}
          label={t('media.withExtractedText')}
          value={metric(formatInteger(stats?.extracted_files ?? 0))}
          hint={unknown || !stats?.total_files
            ? undefined
            : t('media.metrics.extractedShare', { pct: Math.round((stats.extracted_files / stats.total_files) * 100) })}
        />
        <StatCard
          loading={isLoading}
          tone="warning"
          icon={UploadIcon}
          label={t('media.metrics.uploads24h')}
          value={metric(formatInteger(stats?.uploads_last_24h ?? 0))}
          hint={unknown ? undefined : t('media.metrics.last24hHint')}
        />
        <StatCard
          loading={isLoading}
          tone="neutral"
          icon={BytesIcon}
          label={t('media.metrics.bytes24h')}
          value={metric(humanBytes(stats?.bytes_last_24h ?? 0))}
          hint={unknown ? undefined : t('media.metrics.last24hHint')}
        />
      </div>

      {/* Virus scanning sits above the storage breakdown: an infected upload
          never becomes a stored object, so nothing further down this page can
          account for it. */}
      <ScanPanel />

      <Card withBorder>
        <Stack gap="md">
          <Stack gap="sm">
            <Title order={4}>{t('media.mime.title')}</Title>
            <Text role="bodySmall" c={MUTED}>{t('media.mime.description')}</Text>
          </Stack>
          {byMime.length === 0
            ? <Text role="bodySmall" c={MUTED}>{isLoading ? t('common.loading') : t('media.mime.empty')}</Text>
            : (
              <Group gap="lg" align="flex-start" wrap="wrap">
                <div style={{ flex: '0 0 240px', minWidth: 240 }}>
                  <DonutChart h={220} data={mimeSlices} valueFormatter={humanBytes} withLegend={false} />
                </div>
                <div style={{ flex: 1, minWidth: 280, overflowX: 'auto' }}>
                  <DataTable<Indexed<MimeRow>>
                    records={byMime.slice(0, 12) as Indexed<MimeRow>[]}
                    idAccessor="mime_type"
                    emptyState={t('media.mime.empty')}
                    columns={[
                      { accessor: 'mime_type', title: t('media.mime.columns.type'), render: (r) => <Text role="bodySmall">{r.mime_type}</Text> },
                      { accessor: 'file_count', title: t('media.columns.files'), textAlign: 'right', render: (r) => formatInteger(r.file_count) },
                      { accessor: 'total_bytes', title: t('media.columns.bytes'), textAlign: 'right', render: (r) => humanBytes(r.total_bytes) }
                    ]}
                  />
                </div>
              </Group>
            )}
        </Stack>
      </Card>

      {/* The chips and the pills sit between the metrics and the table, so
          what is narrowing the list is visible without opening the drawer. */}
      <KpiFilterRow items={kpiItems} value={kpi} onChange={(next) => { setKpi((next ?? 'all') as KpiKey); setPage(1); }} />
      <ActiveFilterPills pills={pills} onRemove={removePill} onClear={clearAllFilters} />

      <TableShell
        title={t('media.tableTitle')}
        totalLabel={
          capped
            ? t('media.totalLabelCapped', { shown: filtered.length, total: all.length })
            : t('media.totalLabel', { shown: filtered.length, total: all.length })
        }
        search={query}
        onSearch={(v) => { setQuery(v); setPage(1); }}
        searchPlaceholder={t('media.searchPlaceholder')}
        filters={
          <Group gap="sm" wrap="nowrap">
            <FilterDrawer fields={filterFields} values={filters} onApply={(next) => { setFilters(next); setPage(1); }} />
          </Group>
        }
        view={view}
        onViewChange={setView}
        loading={isLoading}
        isEmpty={filtered.length === 0}
        empty={<EmptyState title={t('media.empty')} description={hasActiveFilters ? t('common.tryClearingFilters') : t('media.emptyRows')} action={hasActiveFilters ? <Button size="xs" variant="outline" onClick={clearAllFilters}>{t('common.clearFilters')}</Button> : undefined} />}
        error={error
          ? (
            <Alert color="error" title={t('media.loadError')}>
              <Group gap="sm">
                <Text role="bodySmall">{error instanceof Error ? error.message : t('common.unknownError')}</Text>
                <Button size="xs" variant="outline" onClick={() => void refetch()}>{t('action.retry')}</Button>
              </Group>
            </Alert>
          )
          : null}
        pagination={
          <Stack gap="sm" style={{ width: '100%' }}>
            {capped && <Alert color="info" variant="light">{t('media.capHint')}</Alert>}
            <TableFooter
              page={page}
              pageSize={pageSize}
              total={filtered.length}
              capped={capped}
              onPageChange={setPage}
              onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
            />
          </Stack>
        }
      >
        {view === 'table' && (
          <DataTable<Indexed<Row>>
            records={paged as Indexed<Row>[]}
            loading={isLoading}
            idAccessor="organisation_id"
            emptyState={t('media.emptyRows')}
            onRowClick={(r) => nav(`/tenants/${r.organisation_id}`)}
            columns={[
              { accessor: 'organisation_id', title: t('media.columns.tenant'), render: (r) => <TenantName id={r.organisation_id} /> },
              { accessor: 'file_count', title: t('media.columns.files'), textAlign: 'right', sortable: true, render: (r) => formatInteger(r.file_count) },
              { accessor: 'total_bytes', title: t('media.columns.bytes'), textAlign: 'right', sortable: true, render: (r) => humanBytes(r.total_bytes) },
              {
                accessor: 'with_text',
                title: t('media.columns.extracted'),
                textAlign: 'right',
                render: (r) => `${formatInteger(r.with_text)} (${r.file_count > 0 ? Math.round((r.with_text / r.file_count) * 100) : 0}%)`
              },
              {
                // Derived from the two totals already on the page: which
                // tenants the bill is actually being run up by.
                accessor: 'share',
                title: t('media.columns.share'),
                textAlign: 'right',
                render: (r) => {
                  const total = stats?.total_bytes ?? 0;
                  return total > 0
                    ? <Text role="bodySmall">{`${((r.total_bytes / total) * 100).toFixed(1)}%`}</Text>
                    : <Text role="caption" c={MUTED}>{t('common.notAvailable')}</Text>;
                }
              },
              {
                accessor: 'actions',
                title: t('common.actions'),
                width: 64,
                textAlign: 'right',
                render: (r) => (
                  <RowMenu
                    actions={[
                      { id: 'tenant', label: t('media.actions.openTenant'), onSelect: () => nav(`/tenants/${r.organisation_id}`) },
                      { id: 'kb', label: t('media.actions.viewKb'), onSelect: () => nav('/media/kb') }
                    ]}
                  />
                )
              }
            ]}
          />
        )}
        {view === 'list' && (
          <Stack gap="sm">
            {paged.map((r) => (
              <button key={r.organisation_id} type="button" onClick={() => nav(`/tenants/${r.organisation_id}`)} className="rr-list-row">
                <Group gap="md" align="center" wrap="nowrap" style={{ width: '100%' }}>
                  <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
                    <TenantName id={r.organisation_id} />
                    <Text role="caption" c={MUTED}>
                      {t('media.list.meta', { files: formatInteger(r.file_count), withText: formatInteger(r.with_text) })}
                    </Text>
                  </Stack>
                  <Stack gap="xs" style={{ textAlign: 'right', flexShrink: 0 }}>
                    <Text role="bodySmall" fw={600}>{humanBytes(r.total_bytes)}</Text>
                    <Text role="caption" c={MUTED}>{t('media.list.storage')}</Text>
                  </Stack>
                </Group>
              </button>
            ))}
          </Stack>
        )}
        {view === 'cards' && (
          <EntityCardGrid<Row>
            records={paged}
            idAccessor="organisation_id"
            cols={3}
            spacing="md"
            onCardClick={(r) => nav(`/tenants/${r.organisation_id}`)}
            renderCard={(r) => (
              <Stack gap="md">
                <Group justify="space-between" align="baseline" wrap="nowrap">
                  <TenantName id={r.organisation_id} />
                  {r.total_bytes >= LARGE_THRESHOLD && <Badge size="xs" color="orange" variant="light">{t('media.card.large')}</Badge>}
                </Group>
                <Group gap="md" style={{ paddingTop: 12, borderTop: '1px solid var(--rr-color-border)' }}>
                  <Stack gap="xs"><Text role="caption" c={MUTED}>{t('media.columns.files')}</Text><Text role="bodySmall" fw={600}>{formatInteger(r.file_count)}</Text></Stack>
                  <Stack gap="xs"><Text role="caption" c={MUTED}>{t('media.columns.bytes')}</Text><Text role="bodySmall" fw={600}>{humanBytes(r.total_bytes)}</Text></Stack>
                  <Stack gap="xs"><Text role="caption" c={MUTED}>{t('media.card.withText')}</Text><Text role="bodySmall" fw={600}>{formatInteger(r.with_text)}</Text></Stack>
                </Group>
              </Stack>
            )}
          />
        )}
      </TableShell>
    </Stack>
  );
}
